import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { ReactiveJsonRoot, mergeComponentCollections, setClipboardData } from '@ea-lab/reactive-json'
import { AuthProvider } from './context/AuthContext'
import { AuthGuard } from './components/auth'
import { AiChatProvider } from './context/AiChatContext'
import { AiChatPanel, AiChatToggleButton } from './components/ai'
import { ReportOverlay, CaptureReceiver } from './components/report'
import { NavbarActions } from './components/navbar'
import { PipelineSettings } from './components/crm/PipelineSettings'
import { NavActiveSync, SidebarNav, OrbitChrome, FavoriteToggle, CornerDock } from './components/shell'
import { routeMapping, publicRoutes, isPageExcluded } from './config/routes'
import { DynamicPage, DynamicPageRouter, DynamicYamlPage, AppErrorBoundary } from './components/core'
import { MiniChart } from './components/charts/MiniChart'
import { BuilderPublicRenderer } from './components/builder/BuilderPublicRenderer'
// Monte la page d'accueil composée dans le builder à l'endroit où le YAML le
// demande (cf. pages/home.yaml). Voir DashboardMount pour la justification.
import { DashboardMount } from './components/builder/DashboardMount'
// Le wiki héberge la page du builder dans son propre shell : une page
// documentaire est une builder_page rangée. Voir BuilderPageMount.
import { BuilderPageMount } from './components/builder/BuilderPageMount'
import { BuilderPageCreateMount } from './components/builder/BuilderPageCreateMount'
// L'en-tête du builder (logo + onglets Pages / Contenus / …), pour qu'un écran
// du builder écrit en rjBuild (/builder/contents) porte le MÊME en-tête que ses
// voisins React, sans composant de plus. Il n'a pas de props.
import { BuilderTopHeader } from './components/builder/BuilderNavTabs'
// Rendu HTML en lecture seule (champ de texte formaté). Écrit pour le builder,
// mais générique : il lit indifféremment une chaîne, un {processed} ou un
// {value} — les trois formes qu'un text_long prend en sortie de decoupled.
// Utilisé hors builder par la fiche de tâche (conversation de l'assistant).
import { BuilderHtmlField } from './components/builder/BuilderHtmlField'
import {
  Modal, ImageLightbox, SelectAllHandler, FormattedDate, PageHeader, DataSyncSaveButton,
  PaginationContainer, PaginationEllipsis, PaginationFirst, PaginationItem,
  PaginationLast, PaginationNext, PaginationPrev,
  DateTimeWithTimezoneField, StarRatingField, PriorityIconField, CreatableSelectField, CreatableTagsField, FuzzySelectField, IntegerField, StableTextField,
  AltchaField, Base64FileField,
  PictoPickerField, ComplexityDotsField, MediaPickerField, FieldStatus, FieldStatusMessage,
  InitialsAvatar, EntityLinkList, GroupMembersPanel, PictoIcon,
  DropBoard, DropColumn, DragCard,
  esFetchReaction, batchRunReaction, stringifyDataReaction, navigateToReaction,
  joinDataReaction, downloadCsvReaction,
  HostMessageData,
} from './components/shared'
import { lazy } from 'react'
const RichTextField = lazy(() =>
  import('./components/shared/fields/RichTextField').then(m => ({ default: m.RichTextField }))
)
import {
  KanbanBoard, KanbanHeader, KanbanTypeBadge, KanbanTaskLink,
  KanbanSubtaskButton, KanbanMoveToTopButton, KanbanDeleteButton, KanbanArchiveButton, KanbanUnarchiveButton,
  KanbanComplexityDots, KanbanPriorityIcon, KanbanDescriptionEye, KanbanTransitionMenu,
  KanbanTagList, KanbanUserAvatar, KanbanReturnTypeLabel, KanbanSyncBridge,
  KanbanListRow,
} from './components/kanban'
import {
  NewTaskModal, TaskEdit, TaskTagsField, TaskTypeBadge, TaskStatusSelector, TaskCreateModal, TaskTransitionBridge, TaskStateSelect,
  TimeLog, Timesheet as TimeSheet, DatePickerField,
  TaskColorBadge, TaskPriorityIcon, TaskAssigneeAvatar, TaskTagsInline,
} from './components/task'
import { ProjectDashboard, ProjectList, ProjectSettingsModal, ProjectTrashList } from './components/project'
import { ConfigurationPage, ConfigurationSidebar, SidebarConfigLink } from './components/admin'
import { EcaEditor, WorkflowModal } from './components/eca'
import { WorkflowGraphEditor } from './components/workflow'
import { ResourceScheduler } from './components/scheduler'
import { MenuAdminTree } from './components/menu'
import {
  DocAccessManager, DocBodyRenderer, DocBreadcrumbs, DocCommentForm,
  DocCommentItem, DocDirectorySettings, DocHistory, DocHubBridge,
  DocPageActions, DocPageLink, DocProjectBadge, DocSpaceActions,
  DocSettings,
  DocSpaceCategoryManager, DocSpaceForm, DocSpaceHeaderMeta,
  DocSpaceSettings, DocTaskLinker, DocTemplateGallery,
  DocToc, DocWideModeButton,
} from './components/doc'
import { leaveTypesToOptionsProcessor } from './dataProcessors/leaveTypesProcessor'
import { wsolutionCallsProcessor } from './dataProcessors/wsolutionCallsProcessor'
import {
  usersProcessor, singleProjectProcessor, projectsProcessor, tasksProcessor,
  commentsProcessor, historyProcessor, taskStatusMapsProcessor,
  taxonomyToOptionsProcessor, taskTypeCollectionProcessor, accountsToOptionsProcessor,
  assignmentsProcessor, publicHolidaysProcessor, customTaskTypesProcessor,
  docDirectoriesProcessor, docSpaceCategoriesProcessor, docPagesProcessor, docCommentsProcessor,
} from './dataProcessors/decoupledApiProcessors'
import { WsolutionAnalyzableContent } from './pages/wsolution/WsolutionPage'
import { WsolutionCallsGraph } from './pages/wsolution/WsolutionCallsGraph'
import {
  FloatingDropdown, FacetList, RangeFacet, DateRangeFacet, DateRangeAutoInterval,
  ChartJs, ActiveFilterChips, Pagination as EsPagination, SearchField,
  LoadingOverlay, ScrollShadow, Carousel, GroupByMonth, JsonTreeView, LucideIcon, PathLink, MapText, FormatDate, FormatCurrency,
  SortableList, JsonMirror, InList, ResizeHandle, JoinText, ChartLoader, DashboardGrid, FilterCount,
  OrderedColumns, ColumnsPicker, RadarChart, UrlSync, ClassMap,
} from './components/builder/primitives'
// Generic markdown renderer: written for the dashboard "Note" widget, used here
// to preview a markdown attachment on a ticket. Nothing about it is builder-specific.
import { MarkdownContent } from './components/builder/MarkdownContent'
import { wsolutionLogsProcessor } from './dataProcessors/wsolutionLogsProcessor'
import { wsolutionDashboardProcessor } from './dataProcessors/wsolutionDashboardProcessor'
import { crmDashboardProcessor } from './dataProcessors/crmDashboardProcessor'
import { userFavoritesProcessor } from './dataProcessors/userFavoritesProcessor'
import { dashboardListProcessor } from './dataProcessors/dashboardListProcessor'
import { collectionToFacetProcessor } from './dataProcessors/collectionToFacetProcessor'
import RequireAdmin from './components/RequireAdmin'
import RequirePermission from './components/RequirePermission'
import { NoticeHost, noticeReaction } from './notices'

const customPlugins = {
    element: {
        KanbanBoard,
        MarkdownContent,
        Modal,
        ImageLightbox,
        BuilderPageCreateMount,
        BuilderTopHeader,
        BuilderHtmlField,
        GroupMembersPanel,
        KanbanHeader,
        KanbanTypeBadge,
        KanbanTaskLink,
        KanbanSubtaskButton,
        KanbanMoveToTopButton,
        KanbanDeleteButton,
        KanbanArchiveButton,
        KanbanUnarchiveButton,
        KanbanComplexityDots,
        KanbanPriorityIcon,
        KanbanDescriptionEye,
        KanbanTransitionMenu,
        KanbanTagList,
        KanbanUserAvatar,
        KanbanReturnTypeLabel,
        KanbanSyncBridge,
        KanbanListRow,
        NewTaskModal,
        PageHeader,
        ProjectList,
        ProjectSettingsModal,
        TaskEdit,
        TaskCreateModal,
        TaskTransitionBridge,
        TaskStateSelect,
        DataSyncSaveButton,
        TaskTagsField,
        TaskTypeBadge,
        StarRatingField,
        PriorityIconField,
        PictoPickerField,
        // Picto d'entité (icône Lucide nommée, emoji hérité en repli).
        PictoIcon,
        ComplexityDotsField,
        MediaPickerField,
        TaskStatusSelector,
        TimeLog,
        TimeSheet,
        // Champ date qui ouvre le calendrier du navigateur au premier clic
        // (`showPicker`) — écrit par la feuille de temps, utilisé aussi par les
        // popovers d'échéance des blocs « Tâches » des fiches CRM.
        DatePickerField,
        WorkflowModal,
        WorkflowGraphEditor,
        EcaEditor,
        ConfigurationPage,
        SidebarConfigLink,
        ProjectDashboard,
        ProjectTrashList,
        SelectAllHandler,
        FormattedDate,
        DateTimeWithTimezoneField,
        IntegerField,
        // Champ texte au curseur stable (cf. StableTextField.jsx) : à préférer
        // à TextField dans toute modale montée en subroot `sharedUpdates`.
        StableTextField,
        AltchaField,
        Base64FileField,
        CreatableSelectField,
        CreatableTagsField,
        FuzzySelectField,
        FilterCount,
        FieldStatus,
        FieldStatusMessage,
        ResourceScheduler,
        MenuAdminTree,
        // La colonne de rubriques de /configuration, pour qu'une page YAML
        // puisse l'afficher telle quelle (cf. pages/journal.yaml).
        ConfigurationSidebar,
        RichTextField,
        DocBreadcrumbs,
        DocHubBridge,
        DocPageActions,
        DocPageLink,
        DocSpaceActions,
        DocToc,
        DocTemplateGallery,
        DocHistory,
        DocBodyRenderer,
        DocSpaceCategoryManager,
        DocSpaceForm,
        DocSpaceHeaderMeta,
        DocSpaceSettings,
        DocSettings,
        DocProjectBadge,
        DocTaskLinker,
        DocAccessManager,
        DocDirectorySettings,
        DocWideModeButton,
        DocCommentForm,
        DocCommentItem,
        WsolutionAnalyzableContent,
        WsolutionCallsGraph,
        // Faceted-search kit (generic ES primitives) — exposed to standalone
        // reactive-json pages, e.g. the WSolution logs page (wsolution/logs.yaml).
        FloatingDropdown,
        FacetList,
        RangeFacet,
        DateRangeFacet,
        // Date range + auto-derived histogram interval, and a generic Chart.js
        // renderer — power the fully-declarative WSolution dashboard page.
        DateRangeAutoInterval,
        ChartJs,
        ChartLoader,
        DashboardGrid,
        SortableList,
        ResizeHandle,
        OrderedColumns,
        ColumnsPicker,
        JoinText,
        JsonMirror,
        InList,
        // Reçoit le contexte poussé par la page qui ENCADRE l'écran (panneau Gmail).
        // See components/shared/ui/HostMessageData.jsx.
        HostMessageData,
        ActiveFilterChips,
        Pagination: EsPagination,
        SearchField,
        LoadingOverlay,
        ScrollShadow,
        Carousel,
        GroupByMonth,
        JsonTreeView,
        LucideIcon,
        PathLink,
        // Classe pilotée par une donnée (statut → recette), et radar SVG : deux
        // primitives écrites pour le builder, génériques, exposées ici pour les
        // écrans YAML hors builder (Observatoire de la qualité web).
        ClassMap,
        RadarChart,
        // Miroir « clé du store → paramètre d'URL » : ce qui rend un filtre
        // partageable par son adresse. Ne rend rien. See UrlSync.jsx.
        UrlSync,
        MapText,
        FormatDate,
        FormatCurrency,
        AiChatToggleButton,
        NavbarActions,
        SidebarNav,
        FavoriteToggle,
        InitialsAvatar,
        MiniChart,
        DashboardMount,
        BuilderPageMount,
        // Glisser-déposer générique d'un tableau à colonnes (kanban CRM).
        DropBoard,
        DropColumn,
        DragCard,
        EntityLinkList,
        PipelineSettings,
        TaskColorBadge,
        TaskPriorityIcon,
        TaskAssigneeAvatar,
        TaskTagsInline,
    },
    pagination: {
        Container: PaginationContainer,
        First: PaginationFirst,
        Prev: PaginationPrev,
        Item: PaginationItem,
        Ellipsis: PaginationEllipsis,
        Next: PaginationNext,
        Last: PaginationLast,
    },
    dataProcessor: {
        'leave-types-to-options': {
            callback: leaveTypesToOptionsProcessor,
            order: 0,
        },
        'decoupled-users': {
            callback: usersProcessor,
            order: 0,
        },
        'decoupled-single-project': {
            callback: singleProjectProcessor,
            order: 0,
        },
        'decoupled-projects': {
            callback: projectsProcessor,
            order: 1,
        },
        'decoupled-tasks': {
            callback: tasksProcessor,
            order: 2,
        },
        'decoupled-comments': {
            callback: commentsProcessor,
            order: 3,
        },
        'decoupled-history': {
            callback: historyProcessor,
            order: 4,
        },
        // Must run before decoupled-taxonomy-to-options to see the raw {id, name}
        // response — though taskStatusMapsProcessor also handles the transformed
        // {value, label} format as a fallback.
        'decoupled-task-status-maps': {
            callback: taskStatusMapsProcessor,
            order: 0,
        },
        'decoupled-taxonomy-to-options': {
            callback: taxonomyToOptionsProcessor,
            order: 0,
        },
        'decoupled-task-type-collection': {
            callback: taskTypeCollectionProcessor,
            order: 0,
        },
        'decoupled-accounts-to-options': {
            callback: accountsToOptionsProcessor,
            order: 0,
        },
        'decoupled-assignments': {
            callback: assignmentsProcessor,
            order: 2,
        },
        'decoupled-public-holidays': {
            callback: publicHolidaysProcessor,
            order: 0,
        },
        'decoupled-custom-task-types': {
            callback: customTaskTypesProcessor,
            order: 0,
        },
        'decoupled-doc-directories': {
            callback: docDirectoriesProcessor,
            order: 1,
        },
        'decoupled-doc-space-categories': {
            callback: docSpaceCategoriesProcessor,
            order: 1,
        },
        'decoupled-doc-pages': {
            callback: docPagesProcessor,
            order: 2,
        },
        'decoupled-doc-comments': {
            callback: docCommentsProcessor,
            order: 3,
        },
        'wsolution-calls': {
            callback: wsolutionCallsProcessor,
            order: 0,
        },
        'wsolution-logs': {
            callback: wsolutionLogsProcessor,
            order: 0,
        },
        'wsolution-dashboard': {
            callback: wsolutionDashboardProcessor,
            order: 0,
        },
        'crm-dashboard': {
            callback: crmDashboardProcessor,
            order: 0,
        },
        'user-favorites': {
            callback: userFavoritesProcessor,
            order: 0,
        },
        'dashboard-list': {
            callback: dashboardListProcessor,
            order: 0,
        },
        // Collection decoupled → vocabulaire d'une facette ES (values / labels /
        // options). Générique : les clés et le préfixe sont déclarés dans l'URL.
        // Ne touche QUE les URLs marquées `asFacet=1`.
        'collection-to-facet': {
            callback: collectionToFacetProcessor,
            order: 0,
        },
        // /tasks needs no processor : the server now ships `contents[]` already
        // assembled (root→descendants, with `_depth` / `_matched`) and flat
        // scalar fields + `<agg>_labels` label tables, so the page reads
        // everything declaratively in YAML.
    },
    reaction: {
        notice: noticeReaction,
        // Error-aware ES fetch (overlay + declarative error message on hard
        // failure) — used by /tasks where `submitData` cannot guarantee the
        // overlay closes and a message shows. See esFetchReaction.js.
        esFetch: esFetchReaction,
        // Traitement serveur par LOTS (rattachement de masse : le rattachement
        // d'une liste de contacts à un segment). Une boucle pilotée par les réponses
        // successives, seule chose qu'une action reactive-json ne sait pas faire.
        // See batchRunReaction.js.
        batchRun: batchRunReaction,
        // Mêmes noms que dans le cœur (2.11), mais anciens paramètres encore
        // utilisés par du contenu : aiguillage selon la forme des paramètres.
        // See stringifyDataReaction.js / joinDataReaction.js.
        stringifyData: stringifyDataReaction,
        // Ancien nom du `setClipboardData` du cœur (même `value`, même `statusPath`
        // "copied" / "failed") : gardé parce que du contenu l'écrit.
        copyToClipboard: setClipboardData,
        // Navigation vers une URL composée, annulée dès qu'un morceau est vide.
        // Gardée pour le contenu existant ; depuis 2.11, le contenu neuf écrit
        // `redirectNow` avec `to` en segments et `required: true` sur ce qui ne
        // doit pas manquer. See navigateToReaction.js.
        navigateTo: navigateToReaction,
        // Concaténation dans une donnée. Depuis 2.11, `submitData`/`fetchData`
        // acceptent une `url` en segments : plus besoin de composer l'URL à part.
        // See joinDataReaction.js.
        joinData: joinDataReaction,
        // Fichier CSV depuis un tableau du store + une liste de colonnes : le
        // YAML ne fabrique pas de fichier. See downloadCsvReaction.js.
        downloadCsv: downloadCsvReaction,
        // `printPage` (window.print()) est une réaction du cœur depuis 2.11 ; la
        // feuille `@media print` d'index.css fait le reste.
    },
}

const plugins = mergeComponentCollections([customPlugins])

/**
 * Public shell — no Drupal auth, no Orbit chrome (AiChatPanel, NavbarActions).
 * Used for CDG31 and any future public-facing micro-sites.
 */
function PublicShell() {
  return (
    <AppErrorBoundary>
      <Routes>
        {publicRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={<route.component hostKind={route.hostKind} readOnly={route.readOnly} />} />
        ))}
      </Routes>
      <NoticeHost />
    </AppErrorBoundary>
  )
}

/**
 * Orbit shell — requires Drupal session auth, renders the full app chrome
 * (AuthGuard, AiChatProvider, AiChatPanel).
 */
function OrbitShell() {
  // Écran EMBARQUÉ (`bare: true` dans routeMapping) : il s'affiche dans le cadre
  // d'un autre site — le panneau Gmail fait 400 px de large. Le chrome global de
  // l'app y recouvre le contenu sans rien apporter : ni le panneau IA, ni le
  // bouton flottant « Signaler » n'ont de sens dans une vitre de cette taille.
  // Comparaison littérale : les routes ainsi marquées n'ont pas de paramètre.
  const { pathname } = useLocation()
  const bare = routeMapping.some((route) => route.bare && route.path === pathname)
  return (
    <AuthProvider>
      <AuthGuard>
        <AiChatProvider>
          <AppErrorBoundary>
            <div>
              <div>
                <Routes>
                  {routeMapping.map((route) => {
                    let element = route.shell ? (
                      <route.shell
                        rjbuild={route.rjbuild}
                        plugins={plugins}
                      />
                    ) : route.component ? (
                      <route.component plugins={plugins} hostKind={route.hostKind} readOnly={route.readOnly} embedded={route.withMenu} />
                    ) : route.dataUrl ? (
                      <DynamicPage
                        rjbuild={route.rjbuild}
                        dataUrl={route.dataUrl}
                        plugins={plugins}
                      />
                    ) : route.dynamicId ? (
                      <DynamicYamlPage
                        rjbuild={route.rjbuild}
                        plugins={plugins}
                        requireEntity={route.requireEntity}
                        requireId={route.requireId}
                        requireEntityField={route.requireEntityField}
                        entityLabel={route.entityLabel}
                      />
                    ) : (
                      <ReactiveJsonRoot
                        rjBuildUrl={route.rjbuild}
                        rjBuildFetchMethod="GET"
                        plugins={plugins}
                      />
                    )
                    // Routes flagged `withMenu` are component routes that should
                    // render inside the Orbit menu chrome (e.g. a dashboard viewed
                    // "in the app" vs. its standalone full-screen route).
                    if (route.withMenu) {
                      element = <OrbitChrome plugins={plugins}>{element}</OrbitChrome>
                    }
                    // Restricted pages are guarded here — the menu only hides
                    // their links, it does not protect the pages themselves.
                    // Prefer `requirePermission`: it is the very permission the
                    // module declares next to the navigation link, so a link
                    // that shows always leads somewhere. `requireAdmin` remains
                    // for what is genuinely reserved to the global role.
                    if (route.requirePermission) {
                      element = <RequirePermission permission={route.requirePermission}>{element}</RequirePermission>
                    }
                    if (route.requireAdmin) {
                      element = <RequireAdmin>{element}</RequireAdmin>
                    }
                    return <Route key={route.path} path={route.path} element={element} />
                  })}
                  {/*
                    Catch-all : le résolveur builder a DÉJÀ tourné, hors
                    AuthGuard, dans AppRouter — on n'arrive ici que parce
                    qu'aucune page builder ne correspond au chemin. Rendre
                    BuilderPublicRenderer une seconde fois déclencherait un
                    second /api/builder/resolve pour rien.
                  */}
                  <Route path="*" element={<DynamicPageRouter plugins={plugins} />} />
                </Routes>
              </div>
              <NavActiveSync />
              {!bare && <AiChatPanel />}
              {!bare && <ReportOverlay plugins={plugins} />}
              <CaptureReceiver />
              <NoticeHost />
            </div>
          </AppErrorBoundary>
        </AiChatProvider>
      </AuthGuard>
    </AuthProvider>
  )
}

function AppRouter() {
  const { pathname } = useLocation()
  const isPublic = publicRoutes.some((r) => {
    // Param routes (e.g. /dashboard/:id) match by segment, not literal string.
    if (r.path.includes(':')) {
      return new RegExp('^' + r.path.replace(/:[^/]+/g, '[^/]+') + '$').test(pathname)
    }
    return pathname === r.path || pathname.startsWith(r.path + '/')
  })
  if (isPublic) return <PublicShell />

  // Territoire du catch-all : aucune route statique ne revendique ce chemin,
  // c'est donc peut-être une page builder publiée à son URL. On tente la
  // résolution AVANT d'entrer dans OrbitShell, sans quoi AuthGuard renverrait
  // un visiteur anonyme sur /login sans jamais interroger le résolveur.
  // Aucune page ne correspond → fallback sur OrbitShell, qui reprend le
  // comportement authentifié habituel (DynamicPageRouter, puis NotFoundPage).
  // La RACINE suit la même règle, à une nuance près : elle EST une route
  // statique (`/` → home.yaml, le tableau de bord), donc `isPageExcluded` la
  // revendique et le test ci-dessous ne la verrait jamais. Or sur le domaine
  // d'un site, `/` doit servir la page d'accueil que ce site déclare
  // (champ `homepage` du groupe, résolu par /api/builder/resolve). Le résolveur
  // tranche : il ne répond que si un site revendique le Host de la requête —
  // sur le domaine d'Orbit il renvoie 404, et le `fallback` rend le tableau de
  // bord habituel. C'est donc bien le BACK qui décide, le front ne connaît
  // aucune liste de domaines.
  if (pathname === '/' || !isPageExcluded(pathname)) {
    return (
      <AppErrorBoundary>
        <BuilderPublicRenderer fallback={<OrbitShell />} />
      </AppErrorBoundary>
    )
  }

  return <OrbitShell />
}

function App() {
  return (
    <Router basename="/">
      <AppRouter />
      {/* Monté au-dessus des deux shells (public et authentifié) : les boutons
          flottants existent des deux côtés, et la barre doit être là avant eux. */}
      <CornerDock />
    </Router>
  )
}

export default App
